/**
 * TimeShiftDialog
 * Dialog for confirming time shift to before/after a deadline.
 * Pattern: Molecule - composes atoms for user interaction.
 */
import { useState, useMemo, useEffect } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  Input,
  Spinner,
  Text,
} from '@billda/design-system';
import { format, addHours, subHours } from 'date-fns';

export interface TimeShiftDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (targetTime: Date) => void;
  deadline: Date;
  mode: 'before' | 'after';
  deadlineLabel: string;
  isPending: boolean;
  isSettingTime?: boolean;
  isTransitioning?: boolean;
}

export function TimeShiftDialog({
  open,
  onClose,
  onConfirm,
  deadline,
  mode,
  deadlineLabel,
  isPending,
  isSettingTime,
  isTransitioning,
}: TimeShiftDialogProps) {
  const [hoursOffset, setHoursOffset] = useState(2);

  // Reset offset when dialog opens
  useEffect(() => {
    if (open) {
      setHoursOffset(2);
    }
  }, [open]);

  const targetTime = useMemo(() => {
    if (mode === 'before') {
      return subHours(deadline, hoursOffset);
    }
    return addHours(deadline, hoursOffset);
  }, [deadline, hoursOffset, mode]);

  const handleConfirm = () => {
    onConfirm(targetTime);
  };

  const handleHoursChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(event.target.value, 10);
    if (!isNaN(value) && value >= 0 && value <= 168) {
      // Max 1 week
      setHoursOffset(value);
    }
  };

  const getProgressText = () => {
    if (isSettingTime) return 'Setting time...';
    if (isTransitioning) return 'Transitioning pots...';
    return 'Processing...';
  };

  return (
    <Dialog
      open={open}
      onOpenChange={next => {
        if (!next && !isPending) {
          onClose();
        }
      }}
    >
      <DialogContent className="max-w-xs">
        <DialogHeader>Shift Time: {deadlineLabel}</DialogHeader>
        <DialogBody className="py-2">
          <div className="py-1">
            <Text variant="body2" color="secondary" className="mb-2">
              Deadline
            </Text>
            <Text variant="body1" className="mb-2 font-medium">
              {format(deadline, 'MMM d, yyyy \u2022 h:mm a')}
            </Text>

            <div className="mt-6 flex items-center gap-2">
              <Text variant="body2">Set time to</Text>
              <Input
                type="number"
                inputSize="sm"
                value={hoursOffset}
                onChange={handleHoursChange}
                min={0}
                max={168}
                className="w-20"
                disabled={isPending}
              />
              <Text variant="body2">
                hour{hoursOffset !== 1 ? 's' : ''}{' '}
                {mode === 'before' ? 'before' : 'after'}
              </Text>
            </div>

            <div className="mt-6">
              <Text variant="body2" color="secondary" className="mb-2">
                Target time
              </Text>
              <Text variant="body1" color="primary" className="font-semibold">
                {format(targetTime, 'MMM d, yyyy \u2022 h:mm a')}
              </Text>
            </div>

            <Alert severity="info" className="mt-6">
              This will also trigger the pot status transitions job.
            </Alert>

            {isPending && (
              <div className="mt-4 flex items-center gap-2">
                <Spinner size={16} />
                <Text variant="body2" color="secondary">
                  {getProgressText()}
                </Text>
              </div>
            )}
          </div>
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={isPending}>
            Cancel
          </Button>
          <Button
            variant="neutral"
            onClick={handleConfirm}
            disabled={isPending}
          >
            Confirm
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
